import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

// ============================================================================
// TYPES
// ============================================================================

export type FinancingScheduleStatus = 'Draft' | 'Sent' | 'Reconciled'

export interface ScheduleLine {
  companyId: string
  clientName: string
  eorId: string
  eorPrefix?: string
  invoiceNumber: string
  estimateId: string
  estimatedAmount: number
  actualAmount?: number
}

export interface FinancingSchedule {
  id: string
  scheduleNumber: string
  lenderId: string
  lenderName?: string
  weekEnding: string
  status: FinancingScheduleStatus
  advanceRate: number
  advanceAmount: number
  estimatedTotal: number
  lines: ScheduleLine[]
  sentDate?: string
  createdDate?: string
}

export interface ListFinancingSchedulesParams {
  lenderId?: string
  status?: FinancingScheduleStatus
}

export interface GenerateScheduleRequest {
  lenderId: string
  weekEnding: string
}

export interface UpdateScheduleLineRequest {
  estimatedAmount?: number
  invoiceNumber?: string
}

export interface UpdateAdvanceRequest {
  advanceRate?: number
  advanceAmount?: number
}

export type ExportFormat = 'csv' | 'xlsx'

// ============================================================================
// SERVICE
// ============================================================================

class FinancingScheduleApiService extends BaseApi {
  private readonly baseUrl = '/payroll/financing-schedules'

  /** GET /payroll/financing-schedules?lenderId=&status= */
  async list(params?: ListFinancingSchedulesParams): Promise<FinancingSchedule[]> {
    const query = new URLSearchParams()
    if (params?.lenderId) query.append('lenderId', params.lenderId)
    if (params?.status) query.append('status', params.status)
    const url = query.toString() ? `${this.baseUrl}?${query.toString()}` : this.baseUrl
    const response = await this.client.get<FinancingSchedule[]>(url)
    return response.data
  }

  /** GET /payroll/financing-schedules/:id */
  // eslint-disable-next-line testing-library/no-await-sync-query
  async getById(id: string): Promise<FinancingSchedule> {
    const response = await this.client.get<FinancingSchedule>(`${this.baseUrl}/${id}`)
    return response.data
  }

  /** POST /payroll/financing-schedules/generate */
  async generate(payload: GenerateScheduleRequest): Promise<FinancingSchedule> {
    const response = await this.client.post<FinancingSchedule>(`${this.baseUrl}/generate`, payload)
    return response.data
  }

  /** PUT /payroll/financing-schedules/:id/lines/:companyId */
  async updateLine(
    id: string,
    companyId: string,
    payload: UpdateScheduleLineRequest,
  ): Promise<FinancingSchedule> {
    const response = await this.client.put<FinancingSchedule>(
      `${this.baseUrl}/${id}/lines/${companyId}`,
      payload,
    )
    return response.data
  }

  /** PUT /payroll/financing-schedules/:id/advance */
  async updateAdvance(id: string, payload: UpdateAdvanceRequest): Promise<FinancingSchedule> {
    const response = await this.client.put<FinancingSchedule>(
      `${this.baseUrl}/${id}/advance`,
      payload,
    )
    return response.data
  }

  /** POST /payroll/financing-schedules/:id/send */
  async send(id: string): Promise<FinancingSchedule> {
    const response = await this.client.post<FinancingSchedule>(`${this.baseUrl}/${id}/send`)
    return response.data
  }

  /** POST /payroll/financing-schedules/:id/reconcile */
  async reconcile(id: string): Promise<FinancingSchedule> {
    const response = await this.client.post<FinancingSchedule>(`${this.baseUrl}/${id}/reconcile`)
    return response.data
  }

  /**
   * GET /payroll/financing-schedules/:id/export?format=csv|xlsx
   * Returns raw Blob for browser download
   */
  async exportFile(id: string, format: ExportFormat): Promise<Blob> {
    try {
      const response = await this.client.get(`${this.baseUrl}/${id}/export`, {
        params: {format},
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error exporting financing schedule:', error)
      throw error
    }
  }
}

export const financingScheduleApi = new FinancingScheduleApiService()
export default financingScheduleApi
